import { produce } from 'immer';
import type { StoreApi } from 'zustand';
import { FeatureMatchStatus } from '@/components/geomatching';
import { Json } from '@/lib/database.types';
import type { LayerColumnCore } from '@/lib/db-row-types';

// LayerTables slice
export type LayerTable = {
  columns: LayerColumnCore[];
  data: { rowId: number; data: Json[]; feature_match?: FeatureMatchStatus; featureId?: string }[];
};

export type LayerTablesState = {
  layerTables: Record<string, LayerTable>;
  setLayerTable: (layerId: string, table: LayerTable) => void;
  updateLayerTable: (layerId: string, table: Partial<LayerTable>) => void;
  clearLayerTables: () => void;
  addTableRow: (layerId: string, row: LayerTable['data'][number]) => void;
  updateTableRow: (layerId: string, row: LayerTable['data'][number]) => void;
  deleteTableRow: (layerId: string, rowId: number) => void;
  addColumn: (layerId: string, column: LayerColumnCore) => void;
  renameColumn: (args: { layerId: string; columnId: number; newName: string }) => void;
  deleteColumn: (layerId: string, columnId: number) => void;
  updateTableCell: (args: {
    layerId: string;
    rowId: number;
    columnName: string;
    value: Json;
  }) => void;
};

export const createLayerTablesSlice = (set: StoreApi<any>['setState']) => ({
  layerTables: {},
  setLayerTable: (layerId: string, table: LayerTable) =>
    set(
      produce((draft: LayerTablesState) => {
        draft.layerTables[layerId] = table;
      })
    ),
  updateLayerTable: (layerId: string, table: Partial<LayerTable>) =>
    set(
      produce((draft: LayerTablesState) => {
        const existingTable = draft.layerTables[layerId];
        if (existingTable) {
          draft.layerTables[layerId] = { ...existingTable, ...table };
        }
      })
    ),
  clearLayerTables: () =>
    set(
      produce((draft: LayerTablesState) => {
        draft.layerTables = {};
      })
    ),
  addTableRow: (layerId: string, row: LayerTable['data'][number]) =>
    set(
      produce((draft: LayerTablesState) => {
        const table = draft.layerTables[layerId];
        if (table) {
          table.data.push(row);
        }
      })
    ),
  updateTableRow: (layerId: string, row: LayerTable['data'][number]) =>
    set(
      produce((draft: LayerTablesState) => {
        const table = draft.layerTables[layerId];
        if (table) {
          const rowIndex = table.data.findIndex(r => r.rowId === row.rowId);
          if (rowIndex !== -1) {
            table.data[rowIndex] = row;
          }
        }
      })
    ),
  deleteTableRow: (layerId: string, rowId: number) =>
    set(
      produce((draft: LayerTablesState) => {
        const table = draft.layerTables[layerId];
        if (table) {
          table.data = table.data.filter(r => r.rowId !== rowId);
        }
      })
    ),
  addColumn: (layerId: string, column: LayerColumnCore) =>
    set(
      produce((draft: LayerTablesState) => {
        const table = draft.layerTables[layerId];
        if (table) {
          table.columns.forEach(c => {
            if (c.position >= column.position) {
              c.position += 1;
            }
          });

          let insertIndex = table.columns.findIndex(c => c.position >= column.position);
          if (insertIndex === -1) {
            insertIndex = table.columns.length;
          }

          table.columns.splice(insertIndex, 0, column);
          table.data.forEach(row => row.data.splice(insertIndex, 0, null));
        }
      })
    ),
  renameColumn: ({
    layerId,
    columnId,
    newName,
  }: {
    layerId: string;
    columnId: number;
    newName: string;
  }) =>
    set(
      produce((draft: LayerTablesState) => {
        const table = draft.layerTables[layerId];
        if (table) {
          const columnIndex = table.columns.findIndex(c => c.id === columnId);
          if (columnIndex !== -1) {
            table.columns[columnIndex].column_name = newName;
          }
        }
      })
    ),
  deleteColumn: (layerId: string, columnId: number) =>
    set(
      produce((draft: LayerTablesState) => {
        const table = draft.layerTables[layerId];
        if (table) {
          const columnIndex = table.columns.findIndex(c => c.id === columnId);
          if (columnIndex !== -1) {
            table.columns.splice(columnIndex, 1);
            table.data.forEach(row => row.data.splice(columnIndex, 1));
          }
        }
      })
    ),
  updateTableCell: ({
    layerId,
    rowId,
    columnName,
    value,
  }: {
    layerId: string;
    rowId: number;
    columnName: string;
    value: Json;
  }) =>
    set(
      produce((draft: LayerTablesState) => {
        const table = draft.layerTables[layerId];
        if (table) {
          const columnIndex = table.columns.findIndex(c => c.column_name === columnName);
          const rowIndex = table.data.findIndex(r => r.rowId === rowId);
          if (columnIndex !== -1 && rowIndex !== -1) {
            table.data[rowIndex].data[columnIndex] = value;
          }
        }
      })
    ),
});
